import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  getFacilityReverses,
  addFacilityReverse,
  submitFacilityReverse,
  approveFacilityReverse,
  rejectFacilityReverse,
  deleteFacilityReverse,
  stopLoadingFacilityReverse,
  startLoadingFacilityReverse,
} from './actions'
import { FacilityReverseParams } from 'app/models/facility-reverse-params'
import { coalesceThunkByKey } from 'app/state/utils/coalesce-thunk'

// Edit is intentionally omitted to preserve the audit trail of
// who-asked-for-what. Delete exists only to clear the draft the ledger leaves
// behind when a submitted reversal is rejected.
export const saveFacilityReverse =
  (savedFacilityReverse: FacilityReverseParams) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(
        `${apiServerUrl}/api/draft-facility-reverses`,
        savedFacilityReverse,
      )
      .then(response => {
        const payload = dispatch(addFacilityReverse(response))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const loadDraftFacilityReverses = async (
  facilityId: string | undefined,
) => {
  const apiServerUrl = config.backendUrl
  if (facilityId) {
    return await client
      .get(`${apiServerUrl}/api/draft-facility-reverses/facility?id=${facilityId}`)
      .then(response => {
        return response.data
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  } else {
    return await client
      .get(`${apiServerUrl}/api/draft-facility-reverses`)
      .then(response => {
        return response.data
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }
}

export const loadSubmittedFacilityReverses = async (
  facilityId: string | undefined,
) => {
  const apiServerUrl = config.backendUrl
  if (facilityId) {
    return await client
      .get(`${apiServerUrl}/api/submitted-facility-reverses/facility?id=${facilityId}`)
      .then(response => {
        return response.data
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  } else {
    return await client
      .get(`${apiServerUrl}/api/submitted-facility-reverses`)
      .then(response => {
        return response.data
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }
}

export const submitForApproval =
  (facilityReverse: FacilityReverseParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/draft-facility-reverses/submit`, {
        id: facilityReverse.id,
      })
      .then(response => {
        const payload = dispatch(submitFacilityReverse(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const approveSelectedFacilityReverse =
  (facilityReverse: FacilityReverseParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-facility-reverses/approve`, {
        id: facilityReverse.id,
      })
      .then(response => {
        const payload = dispatch(approveFacilityReverse(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const rejectSelectedFacilityReverse =
  (facilityReverse: FacilityReverseParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-facility-reverses/reject`, {
        id: facilityReverse.id,
      })
      .then(response => {
        const payload = dispatch(rejectFacilityReverse(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const deleteSelectedFacilityReverse =
  (facilityReverse: FacilityReverseParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(
        `${apiServerUrl}/api/draft-facility-reverses?id=${facilityReverse.id}`,
      )
      .then(response => {
        // The endpoint answers 200 with `{ isSuccess: false, error }` when the
        // archive fails or the draft is already gone — a rejected promise is
        // NOT the only failure mode, so the body has to be inspected. Dropping
        // it from the store on a failed archive would hide a reversal that is
        // still live on the ledger.
        const result: any = response?.data
        if (result && result.isSuccess === false) {
          return { success: false, payload: result.error }
        }
        // The reducer drops it from `facilityReverses`, so the servicing grid
        // and anything else reading the duck update without a refetch.
        const payload = dispatch(
          deleteFacilityReverse({ id: facilityReverse.id }),
        )
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response?.data?.error }
      })
  }

export const loadAllFacilityReverses = coalesceThunkByKey(
  (facilityId?: string | undefined) => async (dispatch: Dispatch) => {
    try {
      dispatch(startLoadingFacilityReverse())
      let allFacilityReverses: FacilityReverseParams[] = []

      const draftFacilityReversesResponse = await loadDraftFacilityReverses(facilityId)
      const submittedFacilityReversesResponse =
        await loadSubmittedFacilityReverses(facilityId)

      const draftFacilityReverses: FacilityReverseParams[] = Array.isArray(
        draftFacilityReversesResponse,
      )
        ? draftFacilityReversesResponse.map(
            (facilityReverse: FacilityReverseParams) => ({
              ...facilityReverse,
              status: 'Draft',
            }),
          )
        : []

      const subFacilityReverses: FacilityReverseParams[] = Array.isArray(
        submittedFacilityReversesResponse,
      )
        ? submittedFacilityReversesResponse.map(
            (a: { facilityReverse: FacilityReverseParams; submitted: string }) => ({
              ...a.facilityReverse,
              submitted: a.submitted,
              status: 'Submitted',
            }),
          )
        : []

      allFacilityReverses = draftFacilityReverses.concat(subFacilityReverses)

      return dispatch(getFacilityReverses(allFacilityReverses))
    } finally {
      dispatch(stopLoadingFacilityReverse())
    }
  },
)
